import {
  getEditorRequestConfig,
  jsonToGeojson,
  flyToOptions,
  debounce
} from "@/utils";
import { getDiagramGeojson } from "../../map/helpers";
import { scenarioModellingLayersConfig, colors } from "../configs";
import { notifyFactory } from "@/utils";

export class BaseController {
  constructor(parent) {
    this.parent = parent;
    this.mapgl = parent.mapgl;
    this.$store = parent.$store;
    this.handlers = {};
  }

  toggleOn(ids) {
    ["nodes", "links"].forEach(type => this.toggleOnBaselayer(type, ids));
  }

  toggleOff(ids) {
    ["nodes", "links"].forEach(type => this.toggleOffBaselayer(type, ids));
  }

  // toggling one base layer
  toggleOnBaselayer(type, ids) {
    const id = ids[type];

    const loadModelLayer = async e => {
      if (e && e.noRequest) return;
      const zoom = this.mapgl.getZoom();
      const config = getEditorRequestConfig(this.parent);

      try {
        this.$store.commit("ADD_SM_LOADING_LAYER", id);
        const url = `objectInfo/${id}?config=${JSON.stringify(
          config
        )}&zoom=${zoom}`;
        const { data } = await this.$store.dispatch("REQUEST", {
          url
        });
        const geojson = jsonToGeojson(Object.keys(data).map(id => data[id]));

        if (!this.mapgl.getLayer(id)) {
          const options = {
            id,
            source: {
              type: "geojson",
              data: geojson
            },
            ...scenarioModellingLayersConfig[type]
          };

          if (this.mapgl.getLayer(ids["nodes"]))
            this.mapgl.addLayer(options, ids["nodes"]);
          else if (this.mapgl.getLayer("gl-draw-polygon-fill-inactive.cold"))
            this.mapgl.addLayer(options, "gl-draw-polygon-fill-inactive.cold");
          else this.mapgl.addLayer(options);
        } else {
          this.mapgl.getSource(id).setData(geojson);

          if (type === "links") {
            this.updateDiagramLayer();
          }
        }
        this.$store.commit("REMOVE_SM_LOADING_LAYER", id);
      } catch (error) {
        console.log(error);
        this.$store.commit("REMOVE_SM_LOADING_LAYER", id);
      }
    };

    this.handlers[id] = debounce(loadModelLayer, 200);
    this.handlers[id]();
    this.mapgl.on("moveend", this.handlers[id]);
  }

  toggleOffBaselayer(type, ids) {
    const id = ids[type];

    if (this.mapgl.getLayer(id)) this.mapgl.removeLayer(id);
    if (this.mapgl.getSource(id)) this.mapgl.removeSource(id);
    this.mapgl.off("moveend", this.handlers[id]);
    delete this.handlers[id];
  }

  // fly to model
  async flyToModel(id) {
    if (!this.$store.state.map.extent[id]) {
      const { data } = await this.$store.dispatch("REQUEST", {
        url: `geom_extent/${id}?`
      });
      const extent = data;

      console.log(extent);

      this.$store.commit("SET_LAYER_EXTENT", { id, extent });
    }

    const extent = this.$store.state.map.extent[id];
    const { center, zoom } = this.mapgl.cameraForBounds(
      [extent.slice(0, 2), extent.slice(2)],
      { padding: { top: 10, bottom: 10, left: 10, right: 10 } }
    );
    this.mapgl.flyTo({
      center,
      zoom,
      ...flyToOptions
    });
  }

  // get calculation data
  async getCalculationData(calcId) {
    const {
      model,
      activeField,
      activeTimeValue,
      diffCalc,
      calcMode
    } = this.$store.state.scenarioModelling;
    const { ids } = model;
    const id = ids["link_calculation_results"];
    const where =
      calcMode === "one"
        ? [
            {
              field: "calculation_id",
              op: "=",
              value: calcId,
              type: "AND"
            }
          ]
        : [
            {
              field: "calculation_id",
              op: "in",
              value: [calcId, diffCalc],
              type: "AND"
            }
          ];
    const config = {
      only: ["link_id", activeField],
      where
    };

    if (activeTimeValue !== null) {
      config.where.push({
        field: "time_value",
        op: "=",
        value: activeTimeValue,
        type: "AND"
      });
    }

    const url = `link_calculation_result/${id}?array=true&config=${JSON.stringify(
      config
    )}`;

    const { data } = await this.$store.dispatch("REQUEST", { url });

    if (data === null) return [];

    const results = Object.keys(data).map(link_id => ({
      link_id,
      [activeField]: data[link_id]
    }));

    this.findAndSetMaxValue(results, activeField);

    return results;
  }

  findAndSetMaxValue(results, field) {
    const max = results.reduce(
      (prev, el) => (prev > Math.abs(el[field]) ? prev : Math.abs(el[field])),
      Math.abs(results[0][field])
    );

    this.$store.commit("CHANGE_SM_CALC_PROP", {
      field: "localMax",
      value: max
    });
  }

  async changeCalculationHandler() {
    try {
      const { timeData, activeCalc } = this.$store.state.scenarioModelling;
      if (!activeCalc) return;

      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "loadingCalc",
        value: activeCalc
      });
      const calcData = await this.getCalculationData(activeCalc, timeData);

      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "calcData",
        value: calcData
      });
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "loadingCalc",
        value: null
      });
    } catch (error) {
      console.log(error);
      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "loadingCalc",
        value: null
      });
    }
  }

  async changeCalcPropHandler(type) {
    const {
      loadingCalc,
      activeCalc,
      diffCalc,
      calcMode,
      activeField,
      timemachinePlaying
    } = this.$store.state.scenarioModelling;

    if (calcMode === "diff" && !diffCalc) return this.clearDiagramLayer();

    if (loadingCalc && !timemachinePlaying) return this.clearDiagramLayer();

    if (!activeCalc || !activeField) return this.clearDiagramLayer();

    if ((type === "calc" || type === "field") && calcMode === "one") {
      await this.fieldChangeHandler();
    }

    await this.changeCalculationHandler();
    this.updateDiagramLayer();
  }

  // update field max value
  async fieldChangeHandler() {
    try {
      const { ids } = this.$store.state.scenarioModelling.model;
      const id = ids["link_calculation_results"];
      const {
        activeField,
        activeCalc,
        manualMax
      } = this.$store.state.scenarioModelling;
      const config = {
        only: [activeField],
        where: [
          {
            field: "calculation_id",
            op: "=",
            value: activeCalc
          }
        ]
      };
      const url = `objectFields/${id}?data_format=aggregation&config=${JSON.stringify(
        config
      )}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      const { max } = data[activeField];

      this.$store.commit("CHANGE_SM_CALC_PROP", {
        field: "fieldMax",
        value: max
      });

      if (!manualMax) {
        this.$store.commit("CHANGE_SM_CALC_PROP", {
          field: "manualMax",
          value: max
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  // update diagram layer setting
  updateDiagramLayer() {
    const { scenarioModelling } = this.$store.state;
    const {
      activeCalc,
      calcMode,
      calcData,
      model,
      activeField,
      maxType,
      diagramWidth,
      showLabels
    } = scenarioModelling;

    let maxValue;

    if (maxType === "manual") {
      maxValue = scenarioModelling.manualMax;
    } else if (calcMode === "diff") {
      maxValue = scenarioModelling.localMax;
    } else {
      maxValue = scenarioModelling[`${maxType}Max`];
    }

    if (!activeCalc) {
      this.clearDiagramLayer();
      return;
    }

    if (!maxValue || calcData.length === 0) {
      const title = "Данные расчета отсутствуют";
      const message =
        "Данные расчета для выбранного расчетного времени по всем объектам отсутствуют.";
      this.parent.$notify(notifyFactory("warning", title, message));

      this.clearDiagramLayer();

      return;
    }

    const { ids } = model;
    const linkId = ids["links"];
    const source = this.mapgl.getSource(linkId);
    if (!source) return;

    const { features } = source._data;
    const config = {
      field: activeField,
      maxValue: maxValue,
      width: diagramWidth
    };

    const linkFeatures = features.map(l => {
      const calcFeature = calcData.find(f => l.properties.id === f.link_id);

      if (!calcFeature) return l;

      const label = calcFeature[activeField]
        ? Number(calcFeature[activeField]).toFixed(1)
        : "";

      return {
        ...l,
        properties: {
          ...l.properties,
          ...calcFeature,
          label
        }
      };
    });

    const diagramGeojson = getDiagramGeojson(linkFeatures, config);

    // add to map
    source.setData({ type: "FeatureCollection", features: linkFeatures });

    const mapSource = this.mapgl.getSource("diagram");

    const diagramColorValue =
      calcMode === "one"
        ? [
            "interpolate",
            ["linear"],
            ["get", activeField],
            0,
            colors[0],
            maxValue / 3,
            colors[1],
            (maxValue / 3) * 2,
            colors[2],
            maxValue,
            colors[3]
          ]
        : ["case", [">", ["get", activeField], 0], colors[0], colors[3]];

    if (mapSource) {
      mapSource.setData(diagramGeojson);
      this.mapgl.setPaintProperty("diagram", "fill-color", diagramColorValue);
      this.mapgl.setPaintProperty(
        "diagram_border",
        "line-color",
        diagramColorValue
      );
      this.mapgl.setPaintProperty(
        "diagram_label",
        "text-color",
        diagramColorValue
      );
    } else {
      this.mapgl.addSource("diagram", {
        type: "geojson",
        data: diagramGeojson
      });
      this.mapgl.addLayer({
        id: "diagram",
        type: "fill",
        source: "diagram",
        paint: {
          "fill-opacity": 0.4,
          "fill-color": diagramColorValue
        }
      });
      this.mapgl.addLayer({
        id: "diagram_border",
        type: "line",
        source: "diagram",
        paint: {
          "line-opacity": 0.8,
          "line-color": diagramColorValue,
          "line-width": 1
        }
      });
      this.mapgl.addLayer({
        id: "diagram_label",
        source: linkId,
        type: "symbol",
        layout: {
          visibility: showLabels ? "visible" : "none",
          "text-field": "{label}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 11,
          "text-pitch-alignment": "map",
          "symbol-placement": "line-center",
          "text-letter-spacing": 0.1,
          "text-offset": [0, 0.7],
          "text-ignore-placement": true
        },
        paint: {
          "text-color": diagramColorValue,
          "text-halo-color": "#fff",
          "text-halo-width": 1
        }
      });
    }
  }

  toggleLabels(value) {
    const propValue = value ? "visible" : "none";

    if (this.mapgl.getLayer("diagram_label")) {
      this.mapgl.setLayoutProperty("diagram_label", "visibility", propValue);
    }
  }

  clearDiagramLayer() {
    if (this.mapgl.getLayer("diagram_label"))
      this.mapgl.removeLayer("diagram_label");
    if (this.mapgl.getLayer("diagram_border"))
      this.mapgl.removeLayer("diagram_border");
    if (this.mapgl.getLayer("diagram")) this.mapgl.removeLayer("diagram");
    if (this.mapgl.getSource("diagram")) this.mapgl.removeSource("diagram");
  }
}
